import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, GoogleAuthProvider } from '../lib/firebase';
import { isEmailAllowed } from '../lib/agents/allowlist';
import { getOrCreateAgentUser } from '../lib/agents/agentUsers';

const AgentAuthContext = createContext(null);

export function AgentAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);
  const [allowlistChecked, setAllowlistChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const checkAllowlistAndSync = useCallback(async (firebaseUser) => {
    if (!firebaseUser?.email) {
      setAllowed(false);
      setAllowlistChecked(true);
      return;
    }
    try {
      const isAllowed = await isEmailAllowed(firebaseUser.email);
      setAllowed(isAllowed);
      setAllowlistChecked(true);
      if (isAllowed) {
        const profile = await getOrCreateAgentUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
        });
        setAgentProfile(profile);
      } else {
        setAgentProfile(null);
      }
    } catch (err) {
      console.error('Allowlist check failed:', err);
      setAllowed(false);
      setAllowlistChecked(true);
    }
  }, []);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setAuthError(null);
      if (firebaseUser) {
        await checkAllowlistAndSync(firebaseUser);
      } else {
        setAllowlistChecked(true);
        setAllowed(false);
        setAgentProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [checkAllowlistAndSync]);

  const signInWithGoogle = useCallback(async () => {
    if (!auth) {
      setAuthError('Firebase is not configured.');
      return;
    }
    setAuthError(null);
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await checkAllowlistAndSync(result.user);
      if (!(await isEmailAllowed(result.user.email))) {
        await firebaseSignOut(auth);
        setUser(null);
        setAllowed(false);
        setAgentProfile(null);
        setAuthError('This email is not authorized to access the agent tools. Please contact your administrator.');
      }
    } catch (err) {
      setAuthError(err.message || 'Sign in failed.');
    } finally {
      setLoading(false);
    }
  }, [checkAllowlistAndSync]);

  const signOut = useCallback(async () => {
    if (auth) await firebaseSignOut(auth);
    setUser(null);
    setAgentProfile(null);
    setAllowed(false);
    setAllowlistChecked(true);
  }, []);

  const value = {
    user,
    agentProfile,
    allowed,
    allowlistChecked,
    loading,
    authError,
    signInWithGoogle,
    signOut,
    isAuthenticated: !!user,
    canAccessAgents: !!user && !!allowed,
  };

  return (
    <AgentAuthContext.Provider value={value}>
      {children}
    </AgentAuthContext.Provider>
  );
}

export function useAgentAuth() {
  const ctx = useContext(AgentAuthContext);
  if (!ctx) throw new Error('useAgentAuth must be used within AgentAuthProvider');
  return ctx;
}
